<template>
  <v-dialog :model-value="modelValue" max-width="600" @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title>{{ settlementForm.id ? $t('settlements.edit') : $t('settlements.add') }}</v-card-title>
      <v-card-text>
        <v-form>
          <v-select
            v-model="settlementForm.investor_id"
            :items="investors"
            item-title="name"
            item-value="id"
            :label="$t('settlements.investor')"
            required
            density="compact"
          />
          <v-autocomplete
            v-model="settlementForm.wallet_id"
            v-model:search="walletSearch"
            :items="filteredWallets"
            item-title="display_name"
            item-value="id"
            :label="$t('settlements.walletColumn')"
            :placeholder="$t('settlements.walletSearchPlaceholder')"
            required
            density="compact"
            class="mt-2"
            auto-select-first
            @update:model-value="(val) => {
              if (!val) {
                walletSearch = ''
              }
            }"
          >
            <template #no-data>
              <div class="pa-2">{{ $t('settlements.noWalletsFound') }}</div>
            </template>
          </v-autocomplete>
          <v-select
            v-model="settlementForm.direction"
            :items="directionOptions"
            item-title="label"
            item-value="value"
            :label="$t('settlements.direction')"
            required
            density="compact"
            class="mt-2"
          />
          <v-select
            v-model="settlementForm.user_id"
            :items="users"
            item-title="email"
            item-value="id"
            :label="$t('settlements.user')"
            required
            density="compact"
            class="mt-2"
          />
          <v-row class="mt-2" no-gutters>
            <v-col cols="6" class="pr-1">
              <v-text-field
                v-model="settlementForm.amount"
                :label="$t('settlements.amount')"
                type="number"
                required
                density="compact"
                @update:model-value="calculateAmountUsdt"
              />
            </v-col>
            <v-col cols="6" class="pl-1">
              <v-text-field
                v-model="settlementForm.rate"
                :label="$t('settlements.rate')"
                type="number"
                required
                density="compact"
                @update:model-value="calculateAmountUsdt"
              />
            </v-col>
          </v-row>
          <v-text-field
            v-model="settlementForm.amount_usdt"
            :label="$t('settlements.amountUsdt')"
            type="number"
            required
            density="compact"
            class="mt-2"
          />
          <v-text-field
            v-model="settlementForm.transaction_date"
            :label="$t('settlements.date')"
            type="date"
            required
            density="compact"
            class="mt-2"
          />
          <v-textarea
            v-model="settlementForm.description"
            :label="$t('settlements.description')"
            rows="3"
            density="compact"
            class="mt-2"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="handleCancel">{{ $t('common.cancel') }}</v-btn>
        <v-btn color="primary" @click="$emit('save', settlementForm)">{{ settlementForm.id ? $t('common.save') : $t('common.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet } from '../../utils/api'
import { getTodayString } from '../../utils/date'

const i18n = useI18n()
const { t } = i18n

interface Props {
  modelValue: boolean
  settlement?: any
  investors: any[]
  wallets: any[]
  users: any[]
}

const props = withDefaults(defineProps<Props>(), {
  settlement: null,
  investors: () => [],
  wallets: () => [],
  users: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'save': [settlement: any]
  'cancel': []
}>()

const walletSearch = ref('')
const directionOptions = computed(() => [
  { label: i18n.t('settlements.toWallet'), value: 'to_wallet' },
  { label: i18n.t('settlements.toInvestor'), value: 'to_investor' }
])

const settlementForm = ref({
  id: null as number | null,
  investor_id: null as number | null,
  wallet_id: null as number | null,
  user_id: null as number | null,
  amount: 0,
  amount_usdt: 0,
  rate: 0,
  direction: 'to_wallet' as 'to_wallet' | 'to_investor',
  transaction_date: '',
  description: ''
})

const filteredWallets = computed(() => {
  if (!props.wallets || props.wallets.length === 0) return []
  if (!walletSearch.value) return props.wallets
  const search = walletSearch.value.toLowerCase()
  return props.wallets.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

const calculateAmountUsdt = () => {
  if (!settlementForm.value) return
  if (settlementForm.value.amount && settlementForm.value.rate) {
    const amount = parseFloat(settlementForm.value.amount?.toString() || '0')
    const rate = parseFloat(settlementForm.value.rate?.toString() || '0')
    if (!isNaN(amount) && !isNaN(rate) && amount > 0 && rate > 0) {
      settlementForm.value.amount_usdt = Math.round(amount * rate * 100000000) / 100000000
    } else {
      settlementForm.value.amount_usdt = 0
    }
  } else {
    settlementForm.value.amount_usdt = 0
  }
}

const fetchRateFromDB = async (coinId: number | null, created_at?: string | null) => {
  if (!coinId) return null
  
  try {
    let url = `/api/coin_rates/latest?coin_id=${coinId}`
    if (created_at) {
      url += `&date_before=${created_at}`
    }
    const response = await apiGet(url)
    if (response.ok) {
      const rateData = await response.json()
      return rateData.rate
    }
  } catch (error) {
    console.error('Fetch rate from DB error:', error)
  }
  return null
}

// Watch for wallet changes to fetch rate from database
watch(() => settlementForm.value?.wallet_id, async (newWalletId) => {
  if (!newWalletId || !settlementForm.value) return
  
  const wallet = props.wallets.find(w => w.id === newWalletId)
  if (wallet && wallet.coin && wallet.coin.id) {
    const transactionDate = settlementForm.value.transaction_date
      ? new Date(settlementForm.value.transaction_date).toISOString()
      : null
    const rate = await fetchRateFromDB(wallet.coin.id, transactionDate)
    if (rate && settlementForm.value) {
      settlementForm.value.rate = parseFloat(rate.toString())
      calculateAmountUsdt()
    } else if (wallet.usdt_rate && settlementForm.value) {
      // Fallback to wallet's usdt_rate if DB fetch fails
      settlementForm.value.rate = parseFloat(wallet.usdt_rate?.toString() || '0')
      calculateAmountUsdt()
    }
  }
})

// Watch for transaction date changes to refetch rate
watch(() => settlementForm.value?.transaction_date, async (newDate) => {
  if (!newDate || !settlementForm.value || !settlementForm.value.wallet_id) return
  
  const wallet = props.wallets.find(w => w.id === settlementForm.value.wallet_id)
  if (wallet && wallet.coin && wallet.coin.id) {
    const transactionDate = newDate
      ? new Date(newDate).toISOString()
      : null
    const rate = await fetchRateFromDB(wallet.coin.id, transactionDate)
    if (rate && settlementForm.value) {
      settlementForm.value.rate = parseFloat(rate.toString())
      calculateAmountUsdt()
    }
  }
})

// Initialize form when settlement prop changes
watch(() => props.settlement, (newSettlement) => {
  if (newSettlement) {
    // Handle both edit (has id) and new (id is 0 or null) cases
    const isEdit = newSettlement.id && newSettlement.id !== 0
    settlementForm.value = {
      id: isEdit ? newSettlement.id : null,
      investor_id: newSettlement.investor_id || null,
      wallet_id: newSettlement.wallet_id || null,
      user_id: newSettlement.user_id || null,
      amount: newSettlement.amount || 0,
      amount_usdt: newSettlement.amount_usdt || 0,
      rate: newSettlement.rate || 0,
      direction: newSettlement.direction || 'to_wallet',
      transaction_date: newSettlement.transaction_date ? (typeof newSettlement.transaction_date === 'string' && newSettlement.transaction_date.includes('T') 
        ? new Date(newSettlement.transaction_date).toISOString().split('T')[0]
        : newSettlement.transaction_date) : getTodayString(),
      description: newSettlement.description || ''
    }
    // Set wallet search text
    if (newSettlement.wallet_id) {
      const wallet = props.wallets.find(w => w.id === newSettlement.wallet_id)
      walletSearch.value = wallet?.display_name || ''
    } else {
      walletSearch.value = ''
    }
  }
}, { immediate: true })

const handleCancel = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

watch(() => props.modelValue, async (isOpen) => {
  if (isOpen) {
    // Ensure form is initialized when dialog opens
    if (!settlementForm.value) {
      const currentUserId = props.users && props.users.length > 0 ? props.users[0].id : null
      settlementForm.value = {
        id: null,
        investor_id: null,
        wallet_id: null,
        user_id: currentUserId,
        amount: 0,
        amount_usdt: 0,
        rate: 0,
        direction: 'to_wallet',
        transaction_date: getTodayString(),
        description: ''
      }
    }
    if (!props.settlement) {
      // Set defaults when opening for a new settlement (fallback if parent didn't set settlement prop)
      // Try to get user_id from users list (first user as fallback)
      const currentUserId = props.users && props.users.length > 0 ? props.users[0].id : null
      settlementForm.value = {
        id: null,
        investor_id: null,
        wallet_id: null,
        user_id: currentUserId,
        amount: 0,
        amount_usdt: 0,
        rate: 0,
        direction: 'to_wallet',
        transaction_date: getTodayString(),
        description: ''
      }
      walletSearch.value = ''
    }
  } else if (!isOpen && !props.settlement) {
    // Reset form when dialog closes and it's not an edit
    settlementForm.value = {
      id: null,
      investor_id: null,
      wallet_id: null,
      user_id: null,
      amount: 0,
      amount_usdt: 0,
      rate: 0,
      direction: 'to_wallet',
      transaction_date: '',
      description: ''
    }
    walletSearch.value = ''
  }
})
</script>

